/**
 * Avatar-fallback initials for a person: first+last word initial for
 * multi-word names, or the first two characters for a single "word" (e.g. an
 * email used when no display name is set). Used wherever a person's avatar
 * needs a fallback — user search/autocomplete rows, the share modal, profile
 * pages.
 */
export function getAvatarInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/**
 * Formats an array of strings into a natural language list.
 * @param items - Array of strings to format
 * @returns Formatted string (e.g., "item1", "item1 and item2", "item1, item2 and item3")
 */
export function formatList(items: string[]): string {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  const allButLast = items.slice(0, -1).join(", ");
  const last = items[items.length - 1];
  return `${allButLast} and ${last}`;
}

/** Convert snake_case to camelCase. */
export function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/**
 * Build an owner display name from given/family name with a fallback.
 * Works regardless of property casing — callers pass the values directly.
 */
export function getOwnerDisplayName(
  givenName: string | null | undefined,
  familyName: string | null | undefined,
  fallback: string | undefined
): string {
  if (givenName || familyName) {
    return [givenName, familyName].filter(Boolean).join(" ");
  }
  return fallback ?? "Unknown";
}

/**
 * Removes the last path segment after "workflow-submissions/" if present.
 *
 * @param url - The URL to process
 * @returns The URL with the last segment removed if applicable, otherwise the original URL
 *
 * @example
 * // Returns "http://localhost:3000/workbenches/3649/files/workbench/cromwell/update_location_test/workflow-submissions/bamstatsWorkflow"
 * removeWorkflowSubmissionId(
 *   "http://localhost:3000/workbenches/3649/files/workbench/cromwell/update_location_test/workflow-submissions/bamstatsWorkflow/8983cf48-3a11-4a6a-8f53-68e6c9a9d87d"
 * );
 */
export function removeWorkflowSubmissionId(url: string): string {
  const workflowSubmissionsPattern = "workflow-submissions/";
  const workflowSubmissionsIndex = url.indexOf(workflowSubmissionsPattern);

  if (workflowSubmissionsIndex === -1) {
    return url;
  }

  const afterWorkflowSubmissions = url.substring(
    workflowSubmissionsIndex + workflowSubmissionsPattern.length
  );

  // If there's content after "workflow-submissions/" and it contains a "/"
  // (meaning there's a workflow name followed by an ID), remove the last segment
  if (afterWorkflowSubmissions && afterWorkflowSubmissions.includes("/")) {
    const lastSlashIndex = url.lastIndexOf("/");
    return url.substring(0, lastSlashIndex);
  }

  return url;
}
